import { css } from '@firebolt-dev/css'
import { useEffect, useRef, useState } from 'react'

import { entities } from '../../shared/Entities'
import { tilesets, resolvePresetCells } from '../../shared/Tilesets'
import { Flags } from '../../shared/Flags'
import { Layers, TILE_SIZE } from '../../shared/shared'
import { cls } from '../utils'

const LAYER_OPTIONS = [
  { value: Layers.TERRAIN, label: 'Terrain' },
  { value: Layers.DECOR, label: 'Decor' },
  { value: Layers.OVERLAY, label: 'Overlay' },
]

const tilesetIds = Object.keys(tilesets)

const initialState = {
  open: false,
  tab: 'tiles',
  selectedTilesetId: tilesetIds[0] || null,
  selectionStart: null,
  selectionEnd: null,
  isSelecting: false,
  paintLayer: Layers.TERRAIN,
  colliderEnabled: false,
  collider: { ox: 0, oy: 0, w: 16, h: 16 },
  paintFps: 0,
  paintFrames: [],
  paintType: 'raw',
  paintPresetId: null,
  paintTool: 'brush',
  activePresetId: null,
  selectedEntityName: Object.keys(entities)[0] || null,
  selectedFlagName: Object.keys(Flags)[0] || null,
}

function getSelectedCells(selectionStart, selectionEnd) {
  if (!selectionStart) return []
  const end = selectionEnd ?? selectionStart
  const x0 = Math.min(selectionStart.cx, end.cx)
  const x1 = Math.max(selectionStart.cx, end.cx)
  const y0 = Math.min(selectionStart.cy, end.cy)
  const y1 = Math.max(selectionStart.cy, end.cy)
  const cells = []
  for (let cy = y0; cy <= y1; cy++) {
    for (let cx = x0; cx <= x1; cx++) {
      cells.push([cx, cy])
    }
  }
  return cells
}

function TilesetGrid({
  def,
  presets,
  activePresetId,
  selectionStart,
  selectionEnd,
  isSelecting,
  onCellDown,
  onCellEnter,
  onPresetSelect,
}) {
  const [dims, setDims] = useState(null)
  const [hoveredCell, setHoveredCell] = useState(null)
  const cols = dims?.cols ?? 1
  const rows = dims?.rows ?? 1

  const isSelected = (cx, cy) => {
    if (!selectionStart) return false
    const end = selectionEnd ?? selectionStart
    return (
      cx >= Math.min(selectionStart.cx, end.cx) &&
      cx <= Math.max(selectionStart.cx, end.cx) &&
      cy >= Math.min(selectionStart.cy, end.cy) &&
      cy <= Math.max(selectionStart.cy, end.cy)
    )
  }

  const locationMap = {}
  if (presets) {
    for (const [id, preset] of Object.entries(presets)) {
      if (preset.loc) locationMap[`${preset.loc[0]},${preset.loc[1]}`] = { id, preset }
    }
  }

  return (
    <div
      css={css`
        position: relative;
        user-select: none;
        border: 1px solid rgba(143, 168, 196, 0.2);
      `}
    >
      <img
        src={def.url}
        draggable={false}
        css={css`
          display: block;
          width: 100%;
          image-rendering: pixelated;
        `}
        onLoad={e =>
          setDims({
            cols: Math.round(e.target.naturalWidth / TILE_SIZE),
            rows: Math.round(e.target.naturalHeight / TILE_SIZE),
          })
        }
      />
      <div
        css={css`
          position: absolute;
          inset: 0;
          display: grid;
          grid-template-columns: repeat(${cols}, 1fr);
          grid-template-rows: repeat(${rows}, 1fr);
          cursor: crosshair;
        `}
        onMouseLeave={() => setHoveredCell(null)}
      >
        {Array.from({ length: cols * rows }, (_, i) => {
          const cx = i % cols
          const cy = Math.floor(i / cols)
          const selected = isSelected(cx, cy)
          const locEntry = locationMap[`${cx},${cy}`]
          const isHovered = hoveredCell?.cx === cx && hoveredCell?.cy === cy
          return (
            <div
              key={i}
              onMouseDown={e => {
                e.preventDefault()
                if (locEntry && locEntry.id !== activePresetId) {
                  onPresetSelect(locEntry.id, locEntry.preset)
                } else {
                  onCellDown(cx, cy)
                }
              }}
              onMouseEnter={() => {
                setHoveredCell({ cx, cy })
                if (isSelecting) onCellEnter(cx, cy)
              }}
              css={css`
                position: relative;
                box-sizing: border-box;
                outline: 1px solid rgba(255, 255, 255, 0.08);
                background: ${selected ? 'rgba(216, 241, 113, 0.35)' : 'transparent'};
                ${selected ? 'outline-color: rgba(216, 241, 113, 0.6);' : ''}
              `}
            >
              {locEntry && (
                <div
                  css={css`
                    position: absolute;
                    top: 0;
                    right: 0;
                    width: 16px;
                    height: 16px;
                    background: #d8f171;
                    clip-path: polygon(100% 0, 0 0, 100% 100%);
                    opacity: ${locEntry.id === activePresetId ? 1 : 0.35};
                    pointer-events: none;
                  `}
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function LayerPicker({ value, onChange }) {
  return (
    <div
      css={css`
        display: flex;
        gap: 4px;
        button {
          flex: 1;
          border: 1px solid rgba(143, 168, 196, 0.25);
          background: rgba(255, 255, 255, 0.04);
          color: inherit;
          padding: 5px 4px;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          cursor: pointer;
          &.is-active {
            background: #d8f171;
            color: #172014;
            border-color: #d8f171;
          }
        }
      `}
    >
      {LAYER_OPTIONS.map(opt => (
        <button key={opt.value} className={cls(opt.value === value && 'is-active')} onClick={() => onChange(opt.value)}>
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function ColliderConfig({ enabled, collider, onToggle, onChange }) {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 8px;
        .collider-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          cursor: pointer;
          input {
            cursor: pointer;
            accent-color: #d8f171;
          }
        }
        .collider-fields {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px;
        }
        .collider-field {
          display: flex;
          flex-direction: column;
          gap: 3px;
          font-size: 11px;
          opacity: 0.7;
          input {
            background: rgba(255, 255, 255, 0.06);
            border: 1px solid rgba(143, 168, 196, 0.2);
            color: #f4f8ff;
            padding: 4px 6px;
            font-size: 11px;
            width: 100%;
            box-sizing: border-box;
          }
        }
      `}
    >
      <label className='collider-toggle'>
        <input type='checkbox' checked={enabled} onChange={e => onToggle(e.target.checked)} />
        Collider
      </label>
      {enabled && (
        <div className='collider-fields'>
          {['ox', 'oy', 'w', 'h'].map(field => (
            <label key={field} className='collider-field'>
              {field}
              <input
                type='number'
                value={collider[field]}
                onChange={e => onChange({ ...collider, [field]: Number(e.target.value) })}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export function Editor({ world }) {
  const [state, setState] = useState(initialState)

  // Sync paint state to world.editor whenever relevant state changes
  useEffect(() => {
    const selectedCells = getSelectedCells(state.selectionStart, state.selectionEnd)
    world.editor.setState({
      open: state.open,
      tab: state.tab,
      selectedTilesetId: state.selectedTilesetId,
      selectedCells,
      paintLayer: state.paintLayer,
      paintCollider: state.colliderEnabled ? state.collider : null,
      paintFps: state.paintFps,
      paintFrames: state.paintFrames,
      paintType: state.paintType,
      paintPresetId: state.paintPresetId,
      paintTool: state.paintTool,
      selectedEntityName: state.selectedEntityName,
      selectedFlagName: state.selectedFlagName,
    })
  }, [world, state])

  // Toggle open
  useEffect(() => {
    function onToggle() {
      setState(curr => ({ ...curr, open: !curr.open }))
    }
    world.on('toggleEditor', onToggle)
    return () => {
      world.off('toggleEditor', onToggle)
      world.editor.setState({ ...initialState, open: false })
    }
  }, [world])

  // End selection on mouseup anywhere
  useEffect(() => {
    if (!state.isSelecting) return
    function onUp() {
      setState(curr => ({ ...curr, isSelecting: false }))
    }
    window.addEventListener('mouseup', onUp)
    return () => window.removeEventListener('mouseup', onUp)
  }, [state.isSelecting])

  if (!state.open) return null

  const def = state.selectedTilesetId ? tilesets[state.selectedTilesetId] : null

  return (
    <div
      className='world-editor'
      css={css`
        position: absolute;
        top: 0;
        left: 0;
        width: min(400px, calc(100vw - 40px));
        max-height: calc(100% - 40px);
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 14px;
        background: rgba(11, 16, 24, 0.92);
        box-shadow: 0 18px 50px rgba(0, 0, 0, 0.35);
        backdrop-filter: blur(10px);
        color: #f4f8ff;
        z-index: 10;
        overflow: hidden;

        .world-editor__tabs {
          display: flex;
          gap: 8px;
        }
        .world-editor__tab {
          border: 1px solid rgba(143, 168, 196, 0.25);
          background: rgba(255, 255, 255, 0.04);
          color: inherit;
          padding: 3px 10px;
          cursor: pointer;
        }
        .world-editor__tab.is-active {
          background: #d8f171;
          color: #172014;
          border-color: #d8f171;
        }
        .world-editor__body {
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .world-editor__grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
        }
        .world-editor__card {
          border: 1px solid rgba(143, 168, 196, 0.2);
          background: rgba(255, 255, 255, 0.04);
          color: inherit;
          cursor: pointer;
          padding: 10px;
          text-align: left;
        }
        .world-editor__card.is-active {
          border-color: #d8f171;
          box-shadow: inset 0 0 0 1px #d8f171;
          background: rgba(216, 241, 113, 0.12);
        }
        .world-editor__swatch {
          width: 100%;
          aspect-ratio: 1;
          margin: 0 0 8px;
          background-position: center;
          background-repeat: no-repeat;
          background-size: contain;
          image-rendering: pixelated;
        }
        .world-editor__label {
          font-size: 12px;
          line-height: 1.3;
        }
        .world-editor__section-label {
          margin-bottom: 4px;
        }
      `}
    >
      <div className='world-editor__tabs'>
        {['tiles', 'objects', 'flags'].map(tab => (
          <div
            key={tab}
            className={cls('world-editor__tab', state.tab === tab && 'is-active')}
            onClick={() => setState(curr => ({ ...curr, tab }))}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </div>
        ))}
      </div>

      <div className='world-editor__body'>
        {state.tab === 'tiles' && (
          <>
            {tilesetIds.length === 0 && (
              <div
                css={css`
                  font-size: 12px;
                  opacity: 0.5;
                `}
              >
                No tilesets defined. Add one to src/shared/Tilesets.js
              </div>
            )}

            {/* Tileset selector tabs */}
            {tilesetIds.length > 1 && (
              <div
                css={css`
                  display: flex;
                  gap: 4px;
                  flex-wrap: wrap;
                `}
              >
                {tilesetIds.map(id => (
                  <div
                    key={id}
                    className={cls('world-editor__tab', state.selectedTilesetId === id && 'is-active')}
                    css={css`
                      padding: 5px 8px !important;
                      font-size: 10px !important;
                    `}
                    onClick={() =>
                      setState(curr => ({
                        ...curr,
                        selectedTilesetId: id,
                        selectionStart: null,
                        selectionEnd: null,
                      }))
                    }
                  >
                    {id}
                  </div>
                ))}
              </div>
            )}

            {/* Tileset grid */}
            {def && (
              <TilesetGrid
                def={def}
                presets={def.presets}
                activePresetId={state.activePresetId}
                selectionStart={state.selectionStart}
                selectionEnd={state.selectionEnd}
                isSelecting={state.isSelecting}
                onCellDown={(cx, cy) =>
                  setState(curr => ({
                    ...curr,
                    activePresetId: null,
                    selectionStart: { cx, cy },
                    selectionEnd: { cx, cy },
                    isSelecting: true,
                    colliderEnabled: false,
                    paintLayer: Layers.TERRAIN,
                    paintFps: 0,
                    paintFrames: [],
                    paintType: 'raw',
                    paintPresetId: null,
                  }))
                }
                onCellEnter={(cx, cy) =>
                  setState(curr => ({
                    ...curr,
                    selectionEnd: { cx, cy },
                  }))
                }
                onPresetSelect={(presetId, preset) => {
                  const cells = resolvePresetCells(preset)
                  if (!cells.length && preset.type !== 'autotile') return
                  const iconCell = cells[0] || [0, 0]
                  const isAutotile = preset.type === 'autotile'
                  const isAnimated = !isAutotile && cells.length > 1
                  setState(curr => ({
                    ...curr,
                    activePresetId: presetId,
                    selectionStart: { cx: iconCell[0], cy: iconCell[1] },
                    selectionEnd: { cx: iconCell[0], cy: iconCell[1] },
                    paintLayer: preset.layer ?? curr.paintLayer,
                    colliderEnabled: !!preset.collider,
                    collider: preset.collider ?? curr.collider,
                    paintFps: preset.fps || 0,
                    paintFrames: isAnimated ? cells.slice(1) : [],
                    paintType: isAutotile ? 'autotile' : isAnimated ? 'animated' : 'raw',
                    paintPresetId: isAutotile ? presetId : null,
                  }))
                }}
              />
            )}

            {/* Tool picker */}
            <div>
              <div className='world-editor__section-label'>Tool</div>
              <div className='world-editor__tabs'>
                {['brush', 'fill'].map(tool => (
                  <div
                    key={tool}
                    className={cls('world-editor__tab', state.paintTool === tool && 'is-active')}
                    onClick={() => setState(curr => ({ ...curr, paintTool: tool }))}
                  >
                    {tool.charAt(0).toUpperCase() + tool.slice(1)}
                  </div>
                ))}
              </div>
            </div>

            {/* Layer picker */}
            <div>
              <div className='world-editor__section-label'>Layer</div>
              <LayerPicker
                value={state.paintLayer}
                onChange={paintLayer => setState(curr => ({ ...curr, paintLayer }))}
              />
            </div>

            {/* Collider config */}
            <ColliderConfig
              enabled={state.colliderEnabled}
              collider={state.collider}
              onToggle={colliderEnabled => setState(curr => ({ ...curr, colliderEnabled }))}
              onChange={collider => setState(curr => ({ ...curr, collider }))}
            />
          </>
        )}

        {state.tab === 'objects' && (
          <div className='world-editor__grid'>
            {Object.values(entities).map(entity => (
              <button
                key={entity.name}
                className={cls('world-editor__card', state.selectedEntityName === entity.name && 'is-active')}
                onClick={() => setState(curr => ({ ...curr, selectedEntityName: entity.name }))}
              >
                <div className='world-editor__swatch' style={{ backgroundImage: `url(${entity.icon})` }} />
                <div className='world-editor__label'>{entity.label}</div>
              </button>
            ))}
          </div>
        )}

        {state.tab === 'flags' && (
          <div className='world-editor__grid'>
            {Object.entries(Flags).map(([flagName, flag]) => (
              <button
                key={flagName}
                className={cls('world-editor__card', state.selectedFlagName === flagName && 'is-active')}
                onClick={() => setState(curr => ({ ...curr, selectedFlagName: flagName }))}
              >
                <div className='world-editor__swatch' style={{ background: flag.hex, opacity: 0.7, borderRadius: 4 }} />
                <div className='world-editor__label'>{flag.label}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
